/* Legible axis labels on the blog generator's inline SVG bar charts.

   The generator ships each chart with a fixed "0 0 400 340" viewBox and the
   type size baked in SVG *user units* (11 for axis labels, 13 for bar values).
   In the 720px prose column at a 390px viewport the SVG renders 300px wide, so
   everything scales by 0.75 and those land at 8.3px / 9.8px. Worse, the four
   category labels are wider than their 80-unit bar slot, and SVG <text> has no
   wrapping — measured on the low-ceiling-basement post, "Carl's Place min" and
   "Typical basement" overlapped by 1.6px and read as one run-on word.

   CSS can't fix either half: raising font-size alone makes the overlap worse,
   and there is no SVG text wrap property. So scale the chart's type back up to
   the size it was designed for, break each axis label into tspan lines that fit
   its slot, and grow the viewBox so the extra lines aren't clipped.

   Charts live inside article body HTML, so doing this at render time covers
   every existing post and every future one without rewriting any article. */
(function () {
  var NS = 'http://www.w3.org/2000/svg';
  var MIN_PX = 12;      // smallest text in a chart must render at least this big
  var LINE = 1.15;      // line height, em
  var SLOT_PAD = 8;     // user units of gutter between neighbouring labels

  function attr(el, name) {
    // The HTML parser case-fixes viewBox on SVG, but be tolerant either way.
    return el.getAttribute(name) || el.getAttribute(name.toLowerCase());
  }

  function num(el, name) {
    var v = parseFloat(attr(el, name));
    return isNaN(v) ? 0 : v;
  }

  function fontSize(t) {
    return parseFloat(t.getAttribute('data-gs-size') || t.getAttribute('font-size')) ||
           parseFloat(getComputedStyle(t).fontSize) || 11;
  }

  /* "5'8\"-" + "6'0\"" is one range, not two words -- don't insert a space. */
  function join(parts) {
    return parts.filter(Boolean).reduce(function (a, b) {
      return /[-\u2013\u2014]$/.test(a) ? a + b : a + ' ' + b;
    });
  }

  /* Read a label as words. A label the generator pre-wrapped is a row per child,
     and textContent welds the last word of one row onto the first of the next --
     "Max weight" + "loss (%)" reads back as "Max weightloss (%)". wrap() can
     only break on spaces, so that welded 10-char word then overflowed its
     80-unit slot at any font size and the whole chart collided.

     Walk childNodes, not querySelectorAll('tspan'): the generator writes the
     first row as a bare text node ("Grommets<tspan>& Bungees</tspan>" on the
     grommet-vs-bungee chart), so a tspan-only read drops it entirely. */
  function readText(t) {
    if (!t.querySelector('tspan')) return t.textContent.trim();
    return join([].map.call(t.childNodes, function (n) { return n.textContent.trim(); }));
  }

  /* Re-flow one label into as many tspan lines as it needs. Returns the count. */
  function wrap(t, maxWidth) {
    var words = (t.getAttribute('data-gs-text') || t.textContent).trim().split(/\s+/);
    var x = t.getAttribute('x');
    var probe = document.createElementNS(NS, 'tspan');
    var lines = [];
    var cur = '';

    t.textContent = '';
    t.appendChild(probe);
    words.forEach(function (w) {
      var test = cur ? cur + ' ' + w : w;
      probe.textContent = test;
      if (cur && probe.getComputedTextLength() > maxWidth) {
        lines.push(cur);
        cur = w;
      } else {
        cur = test;
      }
    });
    if (cur) lines.push(cur);

    t.textContent = '';
    lines.forEach(function (line, i) {
      var span = document.createElementNS(NS, 'tspan');
      span.setAttribute('x', x);
      span.setAttribute('dy', i === 0 ? '0' : LINE + 'em');
      span.textContent = line;
      t.appendChild(span);
    });
    return lines.length;
  }

  function relayout(svg) {
    var vb = (attr(svg, 'data-gs-viewbox') || attr(svg, 'viewBox') || '').split(/[\s,]+/).map(Number);
    if (vb.length !== 4 || !vb[2]) return;
    svg.setAttribute('data-gs-viewbox', vb.join(' '));

    // The generator's own line breaks are sometimes the only ones that fit:
    // wrap() can only break on spaces, so a chart whose slot is narrower than
    // a single word ("Retractable" on the grommet-vs-bungee post) ends up worse
    // than it started. Keep the as-authored children so we can put them back.
    // Cache it on first run: the resize handler re-enters with a DOM we already
    // touched, so re-reading innerHTML here would snapshot our own output.
    if (svg._gsAuthored == null) svg._gsAuthored = svg.innerHTML;
    function giveUp() {
      svg.innerHTML = svg._gsAuthored;
      svg.setAttribute('viewBox', vb.join(' '));
    }

    var texts = [].slice.call(svg.querySelectorAll('text'));
    if (!texts.length) return;

    // Some charts hang x/y off the label's first tspan instead of the <text>
    // (the sound-dampening post pre-wraps every axis label that way). All the
    // geometry below reads off the <text>, so a missing y read as 0, no label
    // landed below the baseline rule, and Math.min of an empty axis is
    // Infinity -- which shipped as viewBox="0 0 360 Infinity" and collapsed the
    // whole plot to one bar. Hoist the first row's position onto the <text>.
    texts.forEach(function (t) {
      var first = t.querySelector('tspan');
      if (!first) return;
      ['x', 'y'].forEach(function (a) {
        if (!t.hasAttribute(a) && first.hasAttribute(a)) t.setAttribute(a, first.getAttribute(a));
      });
    });

    var rendered = svg.getBoundingClientRect().width;
    if (!rendered) return;
    var scale = rendered / vb[2];

    // Remember the as-authored size and string so resizes re-flow from source.
    texts.forEach(function (t) {
      if (!t.hasAttribute('data-gs-size')) t.setAttribute('data-gs-size', fontSize(t));
      if (!t.hasAttribute('data-gs-text')) t.setAttribute('data-gs-text', readText(t));
    });

    var sizes = texts.map(fontSize);
    var smallest = Math.min.apply(null, sizes);
    // Only ever scale type up: wide viewports already render these legibly.
    var want = Math.max(1, MIN_PX / (smallest * scale));

    // Axis labels live below the chart's baseline rule; bar value labels sit
    // above it, however short the bar. Some posts' charts pre-wrap a label into
    // two <text> rows 12 units apart, which was fine at font-size 9 but
    // collides once the type is scaled up -- so collapse every stacked row at
    // the same x into one <text> and let wrap() re-break it with em-based line
    // spacing that tracks the font size.
    //
    // Split on the rule, not a type-size band: on the NFPA 701 chart two bars
    // are near-zero, so their value labels landed 21 units above the baseline,
    // inside a "bottom - 3 * size" band. They got merged into the axis label at
    // the same x ("2 Max afterflame (sec)", bold white, over the baseline) and
    // the real axis label was removed.
    var bottom = Math.max.apply(null, texts.map(function (t) { return num(t, 'y'); }));
    var rule = Math.max.apply(null, [0].concat(
      [].map.call(svg.querySelectorAll('line'), function (l) {
        return Math.max(num(l, 'y1'), num(l, 'y2'));
      })));
    // No rule drawn -> fall back to one line-step above the bottom row, which
    // still admits a pre-wrapped second row without reaching the bar values.
    var band = rule || (bottom - 1.6 * Math.max.apply(null, sizes));
    var stacks = {};
    texts.filter(function (t) { return num(t, 'y') > band; })
         .sort(function (a, b) { return num(a, 'y') - num(b, 'y'); })
         .forEach(function (t) {
           var key = t.getAttribute('x');
           (stacks[key] = stacks[key] || []).push(t);
         });

    var axis = Object.keys(stacks).map(function (key) {
      var rows = stacks[key];
      var keep = rows[0];
      if (rows.length > 1 && !keep.hasAttribute('data-gs-merged')) {
        var merged = join(rows.map(function (t) { return t.getAttribute('data-gs-text'); }));
        keep.setAttribute('data-gs-text', merged);
        keep.setAttribute('data-gs-merged', '1');
        rows.slice(1).forEach(function (t) { t.remove(); });
      }
      return keep;
    });

    // No label below the rule means there is nothing to re-flow -- and an empty
    // axis would make baseline (and the viewBox) Infinity. Leave it as authored.
    if (!axis.length) return;

    var baseline = Math.min.apply(null, axis.map(function (t) { return num(t, 'y'); }));
    var xs = axis.map(function (t) { return num(t, 'x'); }).sort(function (a, b) { return a - b; });
    var pitch = vb[2];
    for (var i = 1; i < xs.length; i++) pitch = Math.min(pitch, xs[i] - xs[i - 1]);
    var maxWidth = Math.max(40, pitch - SLOT_PAD);

    // Try the full upscale, then step back until every wrapped line clears its
    // slot. MIN_PX is a target, not a promise: wrap() can only break on spaces,
    // so a long single word sets a hard floor on how big the type can get. On
    // the NFPA 701 chart at 390px, "afterflame" needs 74 of its slot's 80 units
    // at the as-authored 11 -- scaling to 12px pushed it to 103 and it collided
    // with "weightloss" next door. Small type beats unreadable type.
    var factor = want;
    var maxLines = 1;
    for (var pass = 0; pass < 8; pass++) {
      texts.forEach(function (t) {
        t.setAttribute('font-size', +(fontSize(t) * factor).toFixed(2));
      });
      maxLines = 1;
      var worst = 0;
      axis.forEach(function (t) {
        // A label that already fits keeps its single-line form; wrap() no-ops.
        maxLines = Math.max(maxLines, wrap(t, maxWidth));
        [].forEach.call(t.childNodes, function (span) {
          worst = Math.max(worst, span.getComputedTextLength());
        });
      });
      // Overflowing maxWidth only matters once it eats the gutter and touches
      // the neighbouring label, so exit on the real collision threshold.
      if (worst <= pitch - 2) break;
      // Still colliding with no upscale left to give back: our own line breaks
      // are the problem, so hand the chart back as authored rather than ship an
      // overlap the generator didn't have.
      if (factor <= 1) return giveUp();
      factor = Math.max(1, factor * (maxWidth / worst));
    }

    // Grow the viewBox so wrapped lines and their descenders stay inside it.
    // Read the applied size, not fontSize()'s as-authored one -- measuring the
    // unscaled 11 left the second line clipped by 1.3px.
    var axisSize = fontSize(axis[0] || texts[0]) * factor;
    var needed = baseline + (maxLines - 1) * LINE * axisSize + axisSize * 0.35;
    svg.setAttribute('viewBox', vb[0] + ' ' + vb[1] + ' ' + vb[2] + ' ' + Math.max(vb[3], Math.ceil(needed)));
  }

  function run() {
    [].forEach.call(document.querySelectorAll('.prose svg'), function (svg) {
      // A chart, not an inline icon: bars plus more than a couple of labels.
      if (svg.querySelector('rect') && svg.querySelectorAll('text').length > 2) relayout(svg);
    });
  }

  var t;
  function debounced() { clearTimeout(t); t = setTimeout(run, 150); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  // Re-run once the webfont lands: wrap() breaks lines on getComputedTextLength,
  // so measuring in the fallback face picks the wrong breaks (the live NFPA 701
  // chart settled on a taller viewBox than the same page measured locally).
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
  window.addEventListener('resize', debounced);
})();
